/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.ensureVisibleSelection();
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateCaseOnlyUi();
        this.updateBackorderText();
      });
    }

    /**
     * If the currently selected pack option was filtered out for this product type,
     * select the first visible option so price/cart stay in sync.
     */
    ensureVisibleSelection() {
      let changed = false;
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') return;
        const checked = selector.querySelector('input.js-option:checked');
        if (checked) return;
        const first = selector.querySelector('input.js-option');
        if (!first) return;
        first.checked = true;
        changed = true;
      });
      if (changed) {
        this.handleVariantChange({ type: 'change', target: this });
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
        this.updateVariantTitle();
        this.updateCoverageAndSubtotal();
        this.updateCaseOnlyUi();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main')
        || this.section.querySelector('.js-product-form');
      if (!this.productForm) return;

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder / sample notice text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      const backorderProdEl = this.backorder.querySelector('.backorder__product');
      const backorderSuffixEl = this.backorder.querySelector('.backorder__suffix');
      if (!backorderProdEl) return;

      const sampleMessage =
        this.backorder.dataset.sampleMessage ||
        'Sample will be shipped in smaller size available.';
      const title = (this.variant?.title || '').toLowerCase();
      const isSample = title.includes('sample');

      // Sample: always show the fixed shipping notice under variants
      if (this.variant && isSample) {
        backorderProdEl.textContent = sampleMessage;
        if (backorderSuffixEl) backorderSuffixEl.hidden = true;
        this.backorder.hidden = false;
        return;
      }

      // Case / other: show standard backorder copy when inventory is none
      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id] || {};

        if (this.variant.inventory_management && inventory === 'none') {
          const prodTitleEl = this.section.querySelector('.product-title__name')
            || this.section.querySelector('.product-title');
          const baseTitle = prodTitleEl
            ? prodTitleEl.textContent.trim()
            : (this.data.product?.title || '');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${baseTitle}${variantTitle}`;
          if (backorderSuffixEl) backorderSuffixEl.hidden = false;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.section.querySelector('.product-info__price--pack > .price')
        || this.section.querySelector('.product-info__price > .price')
        || this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        const formatted = this.data.formatted[variant.id] || {};
        const shownPrice = formatted.displayPrice || formatted.price;
        if (priceCurrentEl && priceCurrentEl.querySelector('.js-value')) {
          if (variant.compare_at_price > variant.price) {
            const labelEl = priceCurrentEl.querySelector('.js-label');
            if (labelEl) labelEl.textContent = theme.strings.salePrice;
            priceCurrentEl.querySelector('.js-value').innerHTML = shownPrice;
            if (priceWasEl) {
              const wasLabel = priceWasEl.querySelector('.js-label');
              const wasValue = priceWasEl.querySelector('.js-value');
              if (wasLabel) wasLabel.textContent = theme.strings.regularPrice;
              if (wasValue) wasValue.innerHTML = formatted.compareAtPrice || '';
            }
          } else {
            const labelEl = priceCurrentEl.querySelector('.js-label');
            if (labelEl) labelEl.textContent = theme.strings.regularPrice;
            priceCurrentEl.querySelector('.js-value').innerHTML = shownPrice;
            if (priceWasEl) {
              const wasLabel = priceWasEl.querySelector('.js-label');
              const wasValue = priceWasEl.querySelector('.js-value');
              if (wasLabel) wasLabel.textContent = '';
              if (wasValue) wasValue.innerHTML = '';
            }
          }
        }

        // Update unit price, if specified.
        if (unitPriceEl) {
          if (this.variant.unit_price_measurement) {
            const valueEl = this.price.querySelector('.unit-price__price');
            const unitEl = this.price.querySelector('.unit-price__unit');
            const value = this.variant.unit_price_measurement.reference_value;
            const unit = this.variant.unit_price_measurement.reference_unit;
            const unitFormatted = this.data.formatted[this.variant.id] || {};

            if (valueEl) valueEl.innerHTML = unitFormatted.unitPrice || '';
            if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          }
          unitPriceEl.hidden = !this.variant.unit_price_measurement;
        }

        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      const priceDefault = this.price.querySelector('.price__default');
      const priceNoVariant = this.price.querySelector('.price__no-variant');
      if (priceDefault) priceDefault.hidden = !this.variant && !this.preSelection;
      if (priceNoVariant) priceNoVariant.hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }

      const suffix = this.section.querySelector('.js-variant-title-suffix');
      if (suffix) {
        const priceUnit = this.dataset.priceUnit || this.data.priceUnit || 'variant';
        const title = (this.variant && this.variant.title) ? this.variant.title : '';
        const titleDown = title.toLowerCase();

        if (priceUnit === 'sqft') {
          if (titleDown.includes('sample')) {
            suffix.textContent = ' / sample';
          } else {
            suffix.textContent = ' / sqft';
          }
          suffix.hidden = false;
        } else if (priceUnit === 'pack') {
          if (titleDown.includes('piece') || titleDown === 'pcs' || titleDown.includes('pice')) {
            suffix.textContent = ' / pcs';
            suffix.hidden = false;
          } else if (titleDown.includes('case')) {
            suffix.textContent = ' / case';
            suffix.hidden = false;
          } else if (titleDown.includes('sample')) {
            suffix.textContent = ' / sample';
            suffix.hidden = false;
          } else if (title && title !== 'Default Title') {
            suffix.textContent = ` / ${title.toLowerCase()}`;
            suffix.hidden = false;
          } else {
            suffix.textContent = '';
            suffix.hidden = true;
          }
        } else if (title && title !== 'Default Title') {
          suffix.textContent = ` / ${title}`;
          suffix.hidden = false;
        } else {
          suffix.textContent = '';
          suffix.hidden = true;
        }
      }
    }

    /**
     * Appends selected variant name to the product title.
     */
    updateVariantTitle() {
      const titleEl = this.section.querySelector('.js-product-variant-title');
      if (!titleEl) return;

      if (this.variant && this.variant.title && this.variant.title !== 'Default Title') {
        titleEl.textContent = ` - ${this.variant.title}`;
        titleEl.hidden = false;
      } else {
        titleEl.textContent = '';
        titleEl.hidden = true;
      }
    }

    /**
     * Updates coverage UI + subtotal for tile/flooring pack variants.
     */
    updateCoverageAndSubtotal() {
      if (!this.variant) return;

      const formatted = this.data.formatted[this.variant.id];
      if (!formatted) return;

      const totalPriceEl = this.section.querySelector('#getTotalprice');
      if (totalPriceEl) {
        totalPriceEl.setAttribute('data-regular-price', formatted.price);
        totalPriceEl.textContent = formatted.price;
      }

      const packLabel = this.section.querySelector('.js-pack-label');
      if (packLabel && this.variant.title) {
        packLabel.textContent = `${this.variant.title}:`;
      }

      const coverage = formatted.coverage;
      const coverageInput = this.section.querySelector('#bmp-calculated-area');
      const coverageDisplay = this.section.querySelector('#bmp-calculated-area2');
      const coveragePerBoxLabel = this.section.querySelector('.js-coverage-per-box');
      const qtyInput = this.section.querySelector('#quantity');
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

      if (coverage) {
        const totalCoverage = (parseFloat(coverage) * qty).toFixed(2);
        if (coverageInput) {
          coverageInput.value = totalCoverage;
          coverageInput.setAttribute('value', coverage);
          coverageInput.setAttribute('data-fixed-value', coverage);
        }
        if (coverageDisplay) {
          coverageDisplay.textContent = totalCoverage;
          coverageDisplay.setAttribute('value', coverage);
        }
        if (coveragePerBoxLabel) {
          coveragePerBoxLabel.textContent = coverage;
        }
      }

      if (totalPriceEl && formatted.price) {
        const unit = parseFloat(String(formatted.price).replace(/[^0-9.]/g, '')) || 0;
        if (unit > 0 && qty > 1) {
          totalPriceEl.textContent = `$${(unit * qty).toFixed(2)}`;
        }
      }
    }

    /**
     * Hides coverage / waste / calculator UI for Sample/Piece and non-case pack modes.
     */
    updateCaseOnlyUi() {
      if (!this.variant) return;

      const packMode = this.dataset.packMode || this.data.packMode || 'tiles';
      const title = (this.variant.title || '').toLowerCase();
      const isSample = title.includes('sample') || title.includes('sample order');
      const isPiece = title.includes('piece') || title === 'pcs' || title.includes('pice');

      let hide = false;
      if (packMode === 'trim' || packMode === 'accessories' || packMode === 'slabs') {
        hide = true;
      } else if (packMode === 'mosaics') {
        hide = isPiece || isSample;
      } else {
        hide = isSample || isPiece;
      }

      this.section.querySelectorAll('.js-case-only-ui').forEach((el) => {
        el.hidden = hide;
      });

      if (hide) {
        const wasteCheckbox = this.section.querySelector('#waste-factor-new');
        if (wasteCheckbox && wasteCheckbox.checked) {
          wasteCheckbox.checked = false;
          wasteCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.section.querySelectorAll(
        '.js-product-form-main, .js-product-form, .js-instalments-form'
      );

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
